import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabase';
import { criticModelsService } from '../../services/criticModelsService';
import { useNotification } from '../../contexts/NotificationContext';
import { useData } from '../../contexts/DataContext';
import { Company } from '../../types/company.types';
import { providerLabel, providersFromModels } from '../../utils/aiProviders';

interface Props {
  company: Company;
  /** Model ids of the vetted list (ai_models), so their providers are always listed. */
  vettedModels: string[];
}

/**
 * Super admin only: the AI model providers this company may use. Null means
 * every provider; a list restricts the model dropdowns (company AI model,
 * article writer and critics) and the article function refuses other models.
 */
const AllowedProvidersSection: React.FC<Props> = ({ company, vettedModels }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { triggerCompanyFetch } = useData();
  const [openRouterModels, setOpenRouterModels] = useState<string[]>([]);
  const [allowed, setAllowed] = useState<string[] | null>(company.allowed_ai_providers ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAllowed(company.allowed_ai_providers ?? null);
  }, [company.id, company.allowed_ai_providers]);

  useEffect(() => {
    criticModelsService
      .listModels()
      .then((models) => setOpenRouterModels(models.map((m) => m.id)))
      .catch(() => setOpenRouterModels([]));
  }, []);

  const providers = useMemo(
    () => providersFromModels([...vettedModels, ...openRouterModels, ...(allowed ?? [])]),
    [vettedModels, openRouterModels, allowed]
  );
  const allAllowed = allowed === null;
  const saved = company.allowed_ai_providers ?? null;
  const dirty = JSON.stringify(allowed) !== JSON.stringify(saved);
  const empty = allowed !== null && allowed.length === 0;

  const toggleProvider = (provider: string) => {
    setAllowed((prev) => {
      const list = prev ?? providers;
      return list.includes(provider) ? list.filter((p) => p !== provider) : [...list, provider].sort();
    });
  };

  const save = async () => {
    if (empty) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({ allowed_ai_providers: allowed })
        .eq('id', company.id);
      if (error) throw error;
      await triggerCompanyFetch();
      showNotification(t('company.providers_saved'), 'success');
    } catch (error) {
      showNotification(
        t('company.providers_save_failed', {
          message: error instanceof Error ? error.message : String(error),
        }),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="allowed-providers-section" className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{t('company.providers_title')}</h4>
        <p className="text-xs text-gray-500">{t('company.providers_hint')}</p>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={allAllowed}
          onChange={(e) => setAllowed(e.target.checked ? null : [...providers])}
        />
        {t('company.providers_all')}
      </label>
      {!allAllowed && (
        <div className="flex flex-wrap gap-2" data-testid="provider-checkboxes">
          {providers.map((provider) => (
            <label
              key={provider}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${
                allowed!.includes(provider) ? 'border-blue-400 bg-blue-50 text-blue-900' : 'border-gray-300 text-gray-600'
              }`}
            >
              <input
                type="checkbox"
                checked={allowed!.includes(provider)}
                onChange={() => toggleProvider(provider)}
                aria-label={providerLabel(provider)}
              />
              {providerLabel(provider)}
              <span className="text-xs text-gray-400 font-mono">{provider}</span>
            </label>
          ))}
        </div>
      )}
      {empty && <p className="text-xs text-red-600">{t('company.providers_none_selected')}</p>}
      <button
        type="button"
        onClick={save}
        disabled={!dirty || empty || saving}
        className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-semibold disabled:bg-gray-300"
      >
        {t('company.providers_save')}
      </button>
    </section>
  );
};

export default AllowedProvidersSection;
